import { Trash2, Calendar, User, Building2, FileText } from 'lucide-react';
import type { CampaignWithComputed } from '../types/campaign';
import type { UserRole } from '../types/user';
import { canEditCampaign, canDeleteCampaign } from '../lib/permissions';
import { statusTransitionService } from '../services/statusTransitionService';
import { getStatusBadgeClass } from '../utils/statusColors';
import { formatEuro } from '../utils/currency';
import { parseJsonArray } from '../lib/jsonColumn';
import { deriveInfluencerInvoiceStatus } from '../utils/paymentCalculations';
import { useLanguage } from '../contexts/LanguageContext';
import { useModal } from '../contexts/ModalContext';

interface CampaignGridViewProps {
  campaigns: CampaignWithComputed[];
  onEdit: (campaign: CampaignWithComputed) => void;
  onDelete: (id: string) => void;
  userRole: UserRole | null;
  currentUserId?: string;
}

export function CampaignGridView({ campaigns, onEdit, onDelete, userRole, currentUserId }: CampaignGridViewProps) {
  const { t, ts } = useLanguage();
  const { showConfirm } = useModal();

  const sortedCampaigns = [...campaigns].sort((a, b) => {
    const aFullyPaid = a.campaign_status === 'Completed' && a.company_invoice_status === 'Paid' && deriveInfluencerInvoiceStatus(a) === 'Paid';
    const bFullyPaid = b.campaign_status === 'Completed' && b.company_invoice_status === 'Paid' && deriveInfluencerInvoiceStatus(b) === 'Paid';

    if (aFullyPaid && !bFullyPaid) return 1;
    if (!aFullyPaid && bFullyPaid) return -1;
    return 0;
  });

  const ongoingCampaigns = sortedCampaigns.filter(c => {
    return !(c.campaign_status === 'Completed' && c.company_invoice_status === 'Paid' && deriveInfluencerInvoiceStatus(c) === 'Paid');
  });

  const completedCampaigns = sortedCampaigns.filter(c => {
    return c.campaign_status === 'Completed' && c.company_invoice_status === 'Paid' && deriveInfluencerInvoiceStatus(c) === 'Paid';
  });

  const getStatusBadge = (status: string, highlight?: boolean) => {
    const colorClass = highlight ? 'bg-amber-50 text-amber-700' : getStatusBadgeClass(status, 'soft');

    return (
      <span className={`cm-chip ${colorClass}`}>
        {ts(status)}
      </span>
    );
  };

  const getLastDeliverableStatus = (campaign: CampaignWithComputed) => {
    if (!campaign.deliverables) return 'Draft';
    const deliverables = parseJsonArray<{ status?: string }>(campaign.deliverables);
    if (deliverables.length > 0) {
      return deliverables[deliverables.length - 1].status || 'Draft';
    }
    return campaign.delivery_status || 'Draft';
  };

  const getCardBorderColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'border-l-green-500';
      case 'Billing':
        return 'border-l-amber-500';
      case 'In Progress':
        return 'border-l-blue-500';
      case 'Confirmed':
        return 'border-l-sky-400';
      case 'Consultation':
      default:
        return 'border-l-gray-300';
    }
  };

  const renderCampaignCard = (campaign: CampaignWithComputed) => {
    const campaignStatus = campaign.campaign_status;
    const deliverablesStatus = getLastDeliverableStatus(campaign);
    const shouldHighlightMohitInvoice = campaign.company_invoice_status === 'Not Issued' && deliverablesStatus === 'Published';
    const borderColor = getCardBorderColor(campaignStatus);
    const canEdit = canEditCampaign(userRole, campaign, currentUserId);

    return (
      <div
        key={campaign.id}
        onClick={() => canEdit && onEdit(campaign)}
        className={`bg-white rounded-card shadow-card ring-1 ring-gray-100 border border-gray-200 border-l-4 ${borderColor} hover:shadow-card-hover transition-all ${canEdit ? 'cursor-pointer' : 'cursor-default'} overflow-hidden`}
      >
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-semibold text-blue-600 truncate">
                  {campaign.work_code}
                </span>
                {getStatusBadge(campaignStatus)}
              </div>
              <h3 className="text-base font-medium text-gray-900 truncate">
                {campaign.brand || campaign.client}
              </h3>
              {campaign.campaign_ref && (
                <p className="text-sm text-gray-500 truncate">{campaign.campaign_ref}</p>
              )}
            </div>
            {canDeleteCampaign(userRole, campaign, currentUserId) && (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  const confirmed = await showConfirm({
                    title: 'Delete Campaign?',
                    message: `Are you sure you want to delete campaign ${campaign.campaign_ref || campaign.work_code}? This action cannot be undone.`,
                    confirmText: 'Delete',
                    cancelText: 'Cancel',
                    variant: 'danger',
                  });
                  if (confirmed) {
                    onDelete(campaign.id);
                  }
                }}
                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                title="Delete campaign"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="truncate">{campaign.client}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="truncate">{campaign.influencer}</span>
            </div>
            {campaign.agent_name && (
              <div className="flex items-center gap-2 text-sm text-gray-500 italic">
                <span className="w-4 h-4 flex-shrink-0" />
                <span className="truncate text-xs">Agent: {campaign.agent_name}</span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between py-3 border-t border-gray-100">
            <span className="text-lg font-semibold text-gray-900">{formatEuro(campaign.fee)}</span>
            {userRole?.can_view_commissions && (
              <span className="text-sm text-gray-500">
                Commission: {formatEuro(campaign.commission_amount)}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-1" title="Deliverables">
              <FileText className="w-3 h-3 text-gray-400" />
              {getStatusBadge(deliverablesStatus)}
            </div>
            <div title="Client Invoice">
              {getStatusBadge(campaign.company_invoice_status, shouldHighlightMohitInvoice)}
            </div>
            <div title="Influencer Invoice">
              {getStatusBadge(campaign.influencer_invoice_status)}
            </div>
          </div>

          {campaign.days_remaining_to_due !== null && (
            <div className="mt-3 pt-2 border-t border-gray-100">
              <div className="flex items-center gap-1.5 text-xs">
                <Calendar className="w-3 h-3 text-gray-400" />
                <span className={campaign.days_remaining_to_due < 0 ? 'text-red-600 font-semibold' : 'text-gray-500'}>
                  {campaign.days_remaining_to_due > 0
                    ? `${campaign.days_remaining_to_due} days to due`
                    : `${Math.abs(campaign.days_remaining_to_due)} days overdue`}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (campaigns.length === 0) {
    return (
      <div className="bg-white rounded-card shadow-card ring-1 ring-gray-100 p-12 text-center">
        <p className="text-gray-500 text-lg">{t('table.noData')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-card shadow-card ring-1 ring-gray-100 overflow-hidden">
      <div
        className="overflow-y-auto scrollbar-thin p-4"
        style={{ maxHeight: 'calc(100vh - 300px)' }}
      >
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {ongoingCampaigns.map(renderCampaignCard)}
          </div>

          {completedCampaigns.length > 0 && (
            <>
              <div className="flex items-center gap-4 py-2">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Completed Campaigns (All Payments Done)
                </span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 opacity-75">
                {completedCampaigns.map(renderCampaignCard)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
